<template>
    <div>
      <SelectedEmailAndAssignment 
      :emailData="currentEmail"
      :assignment="selectedAssignment"
      :initiallyCollapsed="false"
      @assignmentChanged="onAssignmentChanged"
    />
    
      <div class="comments-section">
        <label for="comments">Comments</label>
        <textarea
            id="comments"
            v-model="comments"
            placeholder="Additional Comments"
        ></textarea>
      </div>
      <div class="forward-primary-banner">
        <span class="forward-primary-label">Forward to Primary Valuer:</span>
        <span class="forward-primary-name">
          {{ selectedAssignment.primaryAppraiser }}
        </span>
      </div>
      
      <AdditionalRecipientsStandalone 
        ref="recipientsComponent"
        :showDescription="true"
        @selection-changed="handleSelectionChanged"
      />
      
      <IsLoadingComponent v-if="showLoading" />
    </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex";
import SelectedAssignment from './assignment/SelectedAssignment.vue';
import SelectedEmailAndAssignment from '@/components/commons/SelectedEmailAndAssignment.vue';
import AdditionalRecipientsStandalone from './AdditionalRecipientsStandalone.vue';
import { markEmailAsCompleteNew } from '../../service/Actions.js';
import { handleRecentActions, fetchAllocationData } from "../../service/engagement.js";
import { ACTION_TYPES } from '@/constants';
import { startTiming, addProcessingTime, addProcessingTimeForError } from '../../utils/metricsHelper.js';
import IsLoadingComponent from '../IsLoadingComponent.vue';
import { getGraphClient } from "@/service/graphClient.js";
import { getGraphToken } from "@/service/TokenService.js";

export default {
  name: "ForwardToAppraiser",
  components: {
    SelectedAssignment,
    SelectedEmailAndAssignment,
    AdditionalRecipientsStandalone,
    IsLoadingComponent
  },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
  },  
  data() {
    return {
      markAsProcessed: true,
      checkedCC: {},
      showLoading: false,
      comments: ''
    };
  },
  computed: {
    ...mapState({
      currentEmail: (state) => state.email.currentEmail,
    }),
    ...mapGetters({
      selectedAssignment: "assignments/selectedAssignment",
    }),
    isValidForSubmit() {
      return !!this.selectedAssignment;
    }
  },
  watch: {
    isValidForSubmit: {
      handler(newValue) {
        this.$emit('isValidForSubmit', newValue);
      },
      immediate: true,
    }
  },
  methods: {
    ...mapActions({
        uploadFiles: "assignments/uploadFiles",
        addMetricsData: "assignments/addMetricsData",
        convertToEmlFormData: "email/convertToEmlFormData",
        add: "notification/add",
    }),
    handleSelectionChanged(data) {
      this.checkedCC = data.checkedCC;
    },
    onAssignmentChanged(newAssignment) {
      // This method is called when assignment changes
    },
    getWorkFileId (value) {
      if (!value) return null
      let arr
      if (value.includes('?')) {
        let boxArr = value.split('?')
        arr = boxArr[0].split('/')
      } else {
        arr = value.split('/')
      }
      return arr && arr.length > 0 ? arr[arr.length - 1] : null
    },
    async forwardToAppraiser() {
      const startTime = startTiming();
      
      const metricsPayload = {
        entityId: this.selectedAssignment.viewEventId,
        fileId: this.selectedAssignment.code,
        actionTaken: ACTION_TYPES.FORWARD_TO_APPRAISER,
        status: 'Completed',
        processingTime: 0
      };

      let finalStatus = 'Completed';
      let errorMessage = null;

      try {
        let selectedCC = this.$refs.recipientsComponent ? this.$refs.recipientsComponent.getSelectedEmails() : [];
        
        // Remove duplicates and filter out primary valuer email to avoid CC'ing them
        const primaryValuerEmail = this.selectedAssignment?.primaryValuerEmail?.toLowerCase();
        selectedCC = [...new Set(
          selectedCC
            .filter(email => email && email.trim()) // Remove empty emails
            .map(email => email.trim().toLowerCase()) // Normalize to lowercase
            .filter(email => email !== primaryValuerEmail) // Don't CC the primary valuer
        )];
        
        this.showLoading = true;
        
        // Check if workFileLink exists
        if (!this.selectedAssignment.workFileLink) {
          this.showLoading = false;
          let msg = {
            message: 'No work file link attached to this assignment',
            type: 'error',
          };
          this.$emit('showNotification', msg);
          finalStatus = 'Error';
          errorMessage = 'No work file link attached to this assignment';
          return;
        }

        let folderId = this.getWorkFileId(this.selectedAssignment.workFileLink);
        if (!folderId) {
          this.showLoading = false;
          this.add({
            message: 'Unable to extract folder ID from work file link. Please check the work file link format.',
            type: 'error',
            timeout:5
          });
          finalStatus = 'Error';
          errorMessage = 'Unable to extract folder ID from work file link';
          return;
        }

        // Upload email to Box
        const formData = await this.convertToEmlFormData();
        let multiFormData = new FormData();
        let document = formData.get('file');
        let documentName = document.name.replace('email_', 'ForwardToAppraiser_' + this.selectedAssignment.code + '_');
        let emlFile = new File([document], documentName, {
          type: document.type,
          lastModified: document.lastModified,
        });
        multiFormData.append('files', emlFile);
        multiFormData.append('folderName', 'correspondence');

        let request = {
          folderId: folderId,
          multiPartFormData: multiFormData,
          config: {
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          },
        };

        let boxResponse = await this.uploadFiles(request);
        if (boxResponse && boxResponse.length > 0) {
          // Forward the email with attachments
          await this.forwardEmailWithAttachments(selectedCC);
          await this.saveFwdToAppraiserActions();
          
          // Mark as complete and show success notification
          if (this.markAsProcessed) {
            markEmailAsCompleteNew(
              'completeNotification',
              'Forward to Appraiser successfully.',
              undefined,
              true
            );
          }
          
          this.add({
            type: 'success',
            message: 'Forward to Appraiser successfully.',
            timeout: 5
          });
      
          this.closeDialog();
        }
      } catch (error) {
        console.error('Error in forward to Appraiser:', error);
        finalStatus = 'Error';
        errorMessage = error.message;
        
        this.add({
          type: 'error',
          message: 'Failed to forward to Appraiser. Please try again.',
          timeout: 5
        });
      } finally {
        this.showLoading = false;
        
        const endTime = performance.now();
        const manualProcessingTime = Math.round(endTime - startTime);
        
        const explicitPayload = {
          entityId: metricsPayload.entityId,
          fileId: metricsPayload.fileId,
          actionTaken: metricsPayload.actionTaken,
          status: finalStatus,
          processingTime: Math.max(0, manualProcessingTime),
          timestamp: new Date().toISOString()
        };
        
        if (errorMessage) {
          explicitPayload.errorMessage = errorMessage;
        }
        
        if (explicitPayload.processingTime == null || isNaN(explicitPayload.processingTime)) {
          explicitPayload.processingTime = 0;
        }
        await this.addMetricsData(explicitPayload);
      }
    },

    async forwardEmailWithAttachments(selectedCC) {
      try {
        // Get Graph client with retry logic
        let client;
        try {
          client = await getGraphClient();
        } catch (tokenError) {
          await getGraphToken(true);
          client = await getGraphClient();
        }

        // Get current item ID and convert to REST format
        const item = Office.context.mailbox.item;
        const messageId = Office.context.mailbox.convertToRestId(
          item.itemId,
          Office.MailboxEnums.RestVersion.v2_0
        );

        // Validate primary valuer email exists
        if (!this.selectedAssignment.primaryValuerEmail) {
          this.showLoading = false;
          this.add({
            message: 'Primary valuer email address is not available for this assignment. Cannot forward to appraiser.',
            type: 'error',
            timeout: 5
          });
          finalStatus = 'Error';
          errorMessage = 'Primary valuer email address is not available for this assignment';
          return;
        }

        // Prepare recipients
        const toRecipients = [{
          emailAddress: {
            name: this.selectedAssignment.primaryValuerName || '',
            address: this.selectedAssignment.primaryValuerEmail
          }
        }];

        // Prepare CC recipients
        const ccRecipients = selectedCC.map(email => ({
          emailAddress: {
            address: email
          }
        }));

        // Step 1: Create a forward draft (preserves all attachments including .msg files)
        const forwardDraft = await client.api(`/me/messages/${messageId}/createForward`).post();
        
        if (!forwardDraft || !forwardDraft.id) {
          throw new Error('Failed to create forward draft');
        }

        // Step 2: Update the draft with proper recipients and comment
        const updatePayload = {
          toRecipients: toRecipients,
          ccRecipients: ccRecipients
        };

        // Only add body/comment if there are comments to add
        if (this.comments) {
          // Preserve the original body content type (HTML or Text)
          const originalContentType = forwardDraft.body?.contentType || 'HTML';
          const originalContent = forwardDraft.body?.content || '';
          
          if (originalContentType.toLowerCase() === 'html') {
            // For HTML content, prepend comments as HTML
            updatePayload.body = {
              contentType: 'HTML',
              content: `<p style="margin-bottom: 10px;"><strong>Comments:</strong><br/>${this.comments.replace(/\n/g, '<br/>')}</p><hr/>${originalContent}`
            };
          } else {
            // For Text content, prepend comments as plain text
            updatePayload.body = {
              contentType: 'Text',
              content: `Comments:\n${this.comments}\n\n${originalContent}`
            };
          }
        }

        await client.api(`/me/messages/${forwardDraft.id}`).patch(updatePayload);

        // Step 3: Send the updated draft
        await client.api(`/me/messages/${forwardDraft.id}/send`).post();

      } catch (error) {
        console.error('Error forwarding email with CC:', error);
        
        // Handle token refresh and retry once
        if (error.message && error.message.includes('token')) {
          try {
            await getGraphToken(true);
            const retryClient = await getGraphClient();
            
            // Retry the entire process
            const messageId = Office.context.mailbox.convertToRestId(
              Office.context.mailbox.item.itemId,
              Office.MailboxEnums.RestVersion.v2_0
            );

            const toRecipients = [{
              emailAddress: {
                name: this.selectedAssignment.primaryValuerName || this.selectedAssignment.primaryAppraiser || '',
                address: this.selectedAssignment.primaryValuerEmail
              }
            }];

            const ccRecipients = selectedCC.map(email => ({
              emailAddress: {
                address: email
              }
            }));

            const forwardDraft = await retryClient.api(`/me/messages/${messageId}/createForward`).post();
            
            const updatePayload = {
              toRecipients: toRecipients,
              ccRecipients: ccRecipients
            };

            // Only add body/comment if there are comments to add
            if (this.comments) {
              // Preserve the original body content type (HTML or Text)
              const originalContentType = forwardDraft.body?.contentType || 'HTML';
              const originalContent = forwardDraft.body?.content || '';
              
              if (originalContentType.toLowerCase() === 'html') {
                // For HTML content, prepend comments as HTML
                updatePayload.body = {
                  contentType: 'HTML',
                  content: `<p style="margin-bottom: 10px;"><strong>Comments:</strong><br/>${this.comments.replace(/\n/g, '<br/>')}</p><hr/>${originalContent}`
                };
              } else {
                // For Text content, prepend comments as plain text
                updatePayload.body = {
                  contentType: 'Text',
                  content: `Comments:\n${this.comments}\n\n${originalContent}`
                };
              }
            }

            await retryClient.api(`/me/messages/${forwardDraft.id}`).patch(updatePayload);
            await retryClient.api(`/me/messages/${forwardDraft.id}/send`).post();
            
          } catch (retryError) {
            console.error('Retry failed:', retryError);
            throw new Error(`Failed to forward email after retry: ${retryError.message}`);
          }
        } else {
          throw new Error(`Failed to forward email: ${error.message}`);
        }
      }
    },

    closeDialog() {
      this.$emit('close');
    },
    async saveFwdToAppraiserActions() {
      const payload =  {
        action: ACTION_TYPES.FORWARD_TO_APPRAISER,
        fileId: this.selectedAssignment?.code,
        entityIds:  [this.selectedAssignment?.viewEventId]
      };
      await handleRecentActions(payload, ACTION_TYPES.FORWARD_TO_APPRAISER);
    },
  }
}
</script>
<style scoped>
.comments-section {
  margin-bottom: 10px;
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 16px;
  vertical-align: middle;
  color: #000;
}
.comments-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.comments-section textarea {
  width: 100%;
  height: 120px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  max-height: 200px;
}
.selected-email-container {
  margin-bottom: 1px;
}

.forward-primary-banner {
  background: #003F2D14;
  border-radius: 4px;
  padding: 4px 3px 4px 4px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.forward-primary-label {
  color: #000000;
  font-family: Calibre-R, Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  margin-right: 8px;
  white-space: nowrap;
}
.forward-primary-name {
  color: #003F2D;
  font-family: Calibre-R, Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  word-break: break-word;
  margin-left: auto;
  text-align: right;
}
</style>